import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, Navigate } from 'react-router-dom';
import { fetchAuthConfig } from '@/env';
import { useStartSignupMutation } from '@/store/billingApi';
import { getErrorMessage } from '@/utils/errorUtils';
import {
  Page,
  Card,
  LogoWrapper,
  Logo,
  LogoText,
  Title,
  Subtitle,
  Form,
  FieldGroup,
  Label,
  Input,
  Select,
  SubmitButton,
  FieldError,
  ErrorMessage,
  Note,
  LoginLinkRow,
  ButtonSpinner,
} from './SignupPage.styled';

const PLANS = ['solo', 'kancelaria'] as const;
type Plan = (typeof PLANS)[number];

const schema = z.object({
  tenantName: z.string().trim().min(1, 'signup.errors.tenantNameRequired'),
  email: z.string().trim().min(1, 'signup.errors.emailRequired').email('signup.errors.emailInvalid'),
  plan: z.enum(PLANS),
});

type FormValues = z.infer<typeof schema>;

const zodResolver: Resolver<FormValues> = (values) => {
  const result = schema.safeParse(values);
  if (result.success) {
    return { values: result.data, errors: {} };
  }
  const errors: Record<string, { type: string; message: string }> = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0];
    if (typeof field === 'string' && !errors[field]) {
      errors[field] = { type: 'validation', message: issue.message };
    }
  }
  return { values: {}, errors };
};

export default function SignupPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [startSignup, { isLoading }] = useStartSignupMutation();

  const [signupEnabled, setSignupEnabled] = useState<boolean | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const planParam = searchParams.get('plan')?.toLowerCase();
  const defaultPlan: Plan = PLANS.includes(planParam as Plan) ? (planParam as Plan) : 'solo';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver,
    defaultValues: { tenantName: '', email: '', plan: defaultPlan },
  });

  useEffect(() => {
    fetchAuthConfig()
      .then((config) => setSignupEnabled(config.signupEnabled))
      .catch(() => setSignupEnabled(false));
  }, []);

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);
    try {
      const result = await startSignup({
        email: values.email,
        tenantName: values.tenantName,
        plan: values.plan,
      }).unwrap();
      window.location.assign(result.checkoutUrl);
    } catch (err) {
      setSubmitError(getErrorMessage(err));
    }
  };

  if (signupEnabled === null) {
    return null;
  }

  if (!signupEnabled) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Page>
      <Card>
        <LogoWrapper>
          <Logo src="/logo_350_350.png" alt="Scribia" />
          <LogoText>Scribia</LogoText>
        </LogoWrapper>
        <Title>{t('signup.title')}</Title>
        <Subtitle>{t('signup.subtitle')}</Subtitle>

        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          {submitError && <ErrorMessage>{submitError}</ErrorMessage>}

          <FieldGroup>
            <Label htmlFor="tenantName">{t('signup.tenantName')}</Label>
            <Input
              id="tenantName"
              type="text"
              autoComplete="organization"
              placeholder={t('signup.tenantNamePlaceholder')}
              {...register('tenantName')}
            />
            {errors.tenantName && <FieldError>{t(errors.tenantName.message ?? '')}</FieldError>}
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="email">{t('signup.email')}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={t('signup.emailPlaceholder')}
              {...register('email')}
            />
            {errors.email && <FieldError>{t(errors.email.message ?? '')}</FieldError>}
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="plan">{t('signup.plan')}</Label>
            <Select id="plan" {...register('plan')}>
              <option value="solo">{t('signup.planSolo')}</option>
              <option value="kancelaria">{t('signup.planKancelaria')}</option>
            </Select>
          </FieldGroup>

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <ButtonSpinner />
                {t('signup.submitting')}
              </>
            ) : (
              t('signup.submit')
            )}
          </SubmitButton>

          <Note>{t('signup.cardNote')}</Note>
        </Form>

        <LoginLinkRow>
          {t('signup.haveAccount')} <Link to="/login">{t('signup.loginLink')}</Link>
        </LoginLinkRow>
      </Card>
    </Page>
  );
}
